import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Layers, Activity, Network, Sparkles, Server, ShieldCheck, Zap, Check } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { useMyServices, serviceProviderScope, serviceLabel } from '@/api/services';
import { OPENNEBULA_SCOPE } from '@/api/opennebula';
import { trackServiceActivate } from '@/lib/analytics-events';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import { KUBERNETES_PROVIDER, KUBERNETES_PRODUCT, kubernetesConsoleRoute } from './kubernetes-activate';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * Managed Kubernetes activation landing — the first-run screen shown when the
 * org has no Kubernetes service. Mirrors WaapActivateLanding exactly: provider
 * resolution via useProviders (wolf, status active), one-click activation via
 * useActivateService, then straight into the Kubernetes service console where
 * the customer creates their first cluster.
 */
export function KubernetesActivateLanding({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  const selfServeActivation = useSelfServeActivation();

  // Only an active `wolf` provider can be provisioned self-serve.
  const wolf = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === KUBERNETES_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  // Prerequisite: Managed Kubernetes runs on Cloud Compute (OpenNebula) — cluster
  // nodes are compute VMs referencing an s10003 resource. Without an active compute
  // service the k8s console can never create a cluster, so gate activation on it.
  // Kubernetes is enabled as an addon on this compute service (backend links the
  // k8s resources + zone to it), so surface which one it binds to.
  const computeService = useMemo(
    () => services.find((s) => serviceProviderScope(s) === OPENNEBULA_SCOPE),
    [services],
  );
  const hasCompute = !!computeService;

  const activate = useActivateService();
  const email = user?.email ?? '';
  const loading = providersLoading || servicesLoading;
  const unavailable = !loading && !wolf;
  const canActivate = !!wolf && hasCompute && !!email && !activate.isPending;
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens the contact-sales dialog.
  const [contactOpen, setContactOpen] = useState(false);

  function handleActivate() {
    if (!wolf || !canActivate) return;
    activate.mutate(
      { serviceName: KUBERNETES_PRODUCT.serviceName, providerId: wolf.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(KUBERNETES_PROVIDER, 'kubernetes');
          show({ tone: 'success', title: 'Managed Kubernetes activated' });
          // Land on the service console (cluster list) where the customer
          // creates their first cluster.
          navigate(kubernetesConsoleRoute(service.id));
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate Managed Kubernetes. Please try again.',
          }),
      },
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        title="Managed Kubernetes"
        description="Managed clusters with node pools, ingress and auto-scaling on the Techev Cloud platform."
        actions={
          onBack ? (
            <Button variant="ghost" onClick={onBack}>
              Back to services
            </Button>
          ) : undefined
        }
      />

      {loading ? (
        <Skeleton style={{ height: 200 }} />
      ) : unavailable ? (
        <Alert tone="warning" title={`${KUBERNETES_PRODUCT.displayName} is not available yet`}>
          {KUBERNETES_PRODUCT.displayName} is not available for self-serve activation on your
          account yet. Please contact our team to enable it.
        </Alert>
      ) : !hasCompute ? (
        <Alert
          tone="info"
          title="Cloud Compute is required first"
          actions={
            <Button
              size="sm"
              startIcon={<Server size={15} />}
              onClick={() => navigate('/services/compute')}
            >
              Activate Cloud Compute
            </Button>
          }
        >
          Managed Kubernetes runs on your Cloud Compute resources — cluster nodes are provisioned as
          virtual machines. Activate Cloud Compute first, then come back to launch your Kubernetes
          clusters.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <Boxes size={30} />
          </span>

          <p style={heroEyebrow}>Managed Kubernetes</p>
          <h2 style={heroTitle}>Activate Managed Kubernetes for your organization</h2>
          <p style={heroLede}>
            One service, two ways to run clusters. Both provision cluster nodes as virtual machines on
            your Cloud Compute — you pick the tier when you create each cluster.
          </p>

          <Alert
            tone="info"
            title={
              computeService
                ? `Linked to Cloud Compute: ${serviceLabel(computeService)}`
                : 'Linked to your Cloud Compute service'
            }
            style={{ maxWidth: 640, margin: 'var(--vn-space-3) auto 0', textAlign: 'left' }}
          >
            Kubernetes clusters draw their CPU, memory and storage from this Cloud Compute service.
            Add-ons such as the cluster-autoscaler and PersistentVolumeClaims (PVCs) provision
            additional VMs and volumes on demand — that usage is counted toward your Cloud Compute
            billing, not billed separately for Kubernetes.
          </Alert>

          <div style={tierGrid}>
            {TIERS.map((t) => (
              <div key={t.title} style={tierCard}>
                <div style={tierHead}>
                  <span style={tierIcon}>{t.icon}</span>
                  <span style={tierTitle}>{t.title}</span>
                  {t.tag ? <span style={tierTag}>· {t.tag}</span> : null}
                  {t.beta ? (
                    <Badge tone="warning" appearance="subtle" style={{ marginLeft: 'auto' }}>
                      Beta
                    </Badge>
                  ) : null}
                </div>
                <p style={tierDesc}>{t.desc}</p>
                <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                  {t.points.map((p) => (
                    <span key={p.text} style={tierPoint}>
                      {p.yes ? (
                        <Check size={13} style={{ color: 'var(--vn-success-text)', flexShrink: 0 }} />
                      ) : (
                        <span style={tierDot} aria-hidden>
                          •
                        </span>
                      )}
                      {p.text}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p style={tierNote}>
            You choose the tier per cluster when you create one — no separate activation.
          </p>

          <Button
            size="lg"
            loading={selfServeActivation && activate.isPending}
            startIcon={<Sparkles size={16} />}
            onClick={selfServeActivation ? handleActivate : () => setContactOpen(true)}
            disabled={selfServeActivation ? !canActivate : false}
            style={{ marginTop: 'var(--vn-space-4)' }}
          >
            {selfServeActivation && activate.isPending
              ? 'Activating…'
              : 'Activate Managed Kubernetes'}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {PERKS.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel={KUBERNETES_PRODUCT.displayName}
        productGroup={PRODUCT_GROUP.cloudService}
      />
    </Container>
  );
}

/** The two cluster tiers a customer can create after activating — chosen per
 *  cluster at create time, NOT a separate activation. */
interface TierPoint {
  text: string;
  yes: boolean;
}
interface Tier {
  title: string;
  tag?: string;
  beta?: boolean;
  icon: ReactNode;
  desc: string;
  points: TierPoint[];
}

const TIERS: Tier[] = [
  {
    title: 'Enterprise',
    tag: 'Standard',
    icon: <ShieldCheck size={16} />,
    desc: 'Production-grade, highly available.',
    points: [
      { text: 'HA control plane (3 masters)', yes: true },
      { text: '99.9% SLA', yes: true },
      { text: 'Full add-ons & ingress', yes: true },
    ],
  },
  {
    title: 'Developer',
    beta: true,
    icon: <Zap size={16} />,
    desc: 'Fast, low-cost — for dev & experiments.',
    points: [
      { text: 'Hosted control plane (we run it)', yes: true },
      { text: 'Single CP · No HA · No SLA', yes: false },
      { text: 'Same add-ons · spins up faster', yes: true },
    ],
  },
];

const tierGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 'var(--vn-space-3)',
  maxWidth: 640,
  margin: 'var(--vn-space-4) auto 0',
  textAlign: 'left',
};
const tierCard: CSSProperties = {
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-4)',
};
const tierHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  marginBottom: 'var(--vn-space-1)',
};
const tierIcon: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-muted, var(--vn-bg-hover))',
  color: 'var(--vn-fg-default)',
  flexShrink: 0,
};
const tierTitle: CSSProperties = { fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' };
const tierTag: CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };
const tierDesc: CSSProperties = {
  color: 'var(--vn-fg-muted)',
  fontSize: 'var(--vn-font-size-sm)',
  margin: '0 0 var(--vn-space-2)',
};
const tierPoint: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  fontSize: 'var(--vn-font-size-sm)',
  color: 'var(--vn-fg-default)',
};
const tierDot: CSSProperties = {
  width: 13,
  textAlign: 'center',
  color: 'var(--vn-fg-muted)',
  flexShrink: 0,
};
const tierNote: CSSProperties = {
  color: 'var(--vn-fg-muted)',
  fontSize: 'var(--vn-font-size-sm)',
  maxWidth: 640,
  margin: 'var(--vn-space-2) auto 0',
};

const PERKS = [
  {
    icon: <Boxes size={16} />,
    title: 'Managed Clusters',
    desc: 'Fully managed Kubernetes control plane — no master node maintenance required.',
  },
  {
    icon: <Layers size={16} />,
    title: 'Node Pools',
    desc: 'Heterogeneous node pools with configurable flavors, counts and auto-scaling policies.',
  },
  {
    icon: <Activity size={16} />,
    title: 'Monitoring',
    desc: 'Built-in cluster and node metrics with real-time health monitoring.',
  },
  {
    icon: <Network size={16} />,
    title: 'Ingress',
    desc: 'Integrated ingress controller with load balancing and TLS termination.',
  },
];

export default KubernetesActivateLanding;
